import type { CityConfig } from "../city-template/types";

export const wilmingtonDeConfig: CityConfig = {
  city: "Wilmington",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "wilmington-de",
  route: "/cities/wilmington-de",
  title: "Emergency Passport Wilmington DE | Expedited Passport Renewal & Same-Day Help | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Wilmington, DE — as fast as 24 hours. Fast passport renewal, same-day passport help, and child passport expediting for New Castle County and northern Delaware travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Wilmington, DE",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast in Wilmington? We help Delaware's largest city and New Castle County travelers get their expedited passports as quickly as 24 hours — no drive to Philadelphia required. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Wilmington%20Delaware%20downtown%20skyline%20at%20dusk%20with%20the%20Christina%20River%20waterfront%20in%20the%20foreground%2C%20modern%20glass%20office%20towers%20of%20the%20financial%20district%20reflecting%20warm%20golden%20and%20amber%20lights%2C%20Brandywine%20Valley%20rolling%20hills%20visible%20in%20the%20background%2C%20clean%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20photorealistic%20style%20with%20soft%20twilight%20sky%20in%20deep%20blue%20and%20orange%2C%20no%20visible%20text%20or%20people&width=1800&height=700&seq=wilmington-de-hero-001&orientation=landscape",
  heroImageAlt: "Wilmington Delaware downtown skyline at dusk with the Christina River waterfront and financial district towers",
  geoPosition: "39.7447;-75.5484",
  geoPlacename: "Wilmington, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "1111 N Independence Mall W, Philadelphia, PA 19106",
    distance: "Approximately 25–35 minutes from Wilmington via I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Wilmington", zips: "19801–19810" },
    { name: "Newark", zips: "19702, 19711, 19713" },
    { name: "Bear", zips: "19701" },
    { name: "Middletown", zips: "19709" },
    { name: "New Castle", zips: "19720" },
    { name: "Glasgow", zips: "19702" },
    { name: "Hockessin", zips: "19707" },
    { name: "Claymont", zips: "19703" },
    { name: "Wilmington Manor", zips: "19720" },
    { name: "Edgemoor", zips: "19809" },
    { name: "Elsmere", zips: "19805" },
    { name: "Pike Creek", zips: "19808" },
    { name: "Talleyville", zips: "19803" },
    { name: "Greenville", zips: "19807" },
    { name: "Brandywine Hundred", zips: "19803" },
    { name: "Stanton", zips: "19804" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Wilmington and northern Delaware departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no need to drive to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Dover, DE", route: "/cities/dover-de" },
    { name: "Fast passport services in Newark, DE", route: "/cities/newark-de" },
    { name: "Emergency passport in Philadelphia", route: "/cities/philadelphia" },
    { name: "Passport renewal in New Jersey", route: "/states/new-jersey" },
    { name: "Delaware passport services overview", route: "/states/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Wilmington remotely via secure registered courier. We do not have a walk-in office in Wilmington — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, typically via the Philadelphia facility 25 minutes away. This is how we achieve 24–48 hour turnaround for Wilmington travelers.",
  fpcOfficeDistance: "Serving all of New Castle County, Delaware",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the entire Wilmington metro area. From the corporate banking district on Market Street to the Brandywine Valley suburbs, from Claymont near the Pennsylvania border to Bear and Glasgow in the south — we cover all of New Castle County with the same fast, reliable passport processing.",
  nearbyCitiesIntro: "We serve all Wilmington neighborhoods and nearby Delaware communities including Newark, New Castle, Bear, Middletown, Hockessin, Claymont, Elsmere, Pike Creek, Talleyville, Greenville, and Brandywine Hundred. If you are anywhere in northern Delaware, we have you covered.",
  howItWorksIntro: "Most Wilmington travelers complete everything online and ship documents overnight. No drive to Philadelphia required. Our emergency and expedited passport process is designed for Delaware corporate travelers, military families, and residents who need fast, reliable results.",
  mistakesIntro: "Wilmington is Delaware's largest city with significant corporate and international business travel. Here are the most common passport mistakes we see from Wilmington and New Castle County residents.",
  testimonialsIntro: "Wilmington and northern Delaware travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Wilmington? Here are the most common ones we hear from New Castle County travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Must drive to Philadelphia — 25–35 min from Wilmington"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Wilmington travelers with immediate departure needs — as fast as 24 hours for urgent international flights out of PHL.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Wilmington.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation for Wilmington residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Wilmington.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes in Delaware.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Wilmington DE", description: "Urgent passport processing for Wilmington travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport Renewal Wilmington DE", description: "Fast passport renewal for Delaware U.S. citizens with a passport issued within the last 15 years." },
    { name: "New Passport Wilmington DE", description: "First-time passport application assistance in Wilmington, Delaware." },
    { name: "Lost or Stolen Passport Wilmington DE", description: "Passport replacement for Wilmington and New Castle County residents." },
    { name: "Child Passport Wilmington DE", description: "Minor passport services for Wilmington families with children under 16." },
    { name: "Name Change Passport Wilmington DE", description: "Passport name change service for Wilmington residents after marriage or legal changes." },
  ],
  testimonials: [
    {
      name: "Robert S.",
      location: "Wilmington, DE",
      rating: 5,
      text: "My corporate flight to London was in 3 days and my passport was expired. Fast Passport Center got me an emergency passport in 36 hours. Absolute lifesaver for our Wilmington banking team.",
      service: "Emergency Passport",
    },
    {
      name: "Karen M.",
      location: "Hockessin, DE",
      rating: 5,
      text: "Needed to renew for a family trip to Italy. The process was seamless — they reviewed everything, and my passport arrived in 4 business days. No drive to Philadelphia needed.",
      service: "Passport Renewal",
    },
    {
      name: "Daniel P.",
      location: "Newark, DE",
      rating: 5,
      text: "Lost my passport before my study abroad program departure. They handled the DS-64 and got me a replacement in 2 days. Couldn't believe how fast it was.",
      service: "Lost Passport",
    },
    {
      name: "Lisa B.",
      location: "Brandywine Hundred, DE",
      rating: 5,
      text: "As a frequent international traveler from northern Delaware, I use Fast Passport Center every renewal. Professional, fast, and I never have to worry about making it to Philadelphia.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "wil-de-1",
      name: "Wilmington Post Office — Main Branch",
      type: "USPS Passport Acceptance Facility",
      address: "200 French St",
      city: "Wilmington",
      state: "DE",
      zip: "19801",
      phone: "(302) 428-7565",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Wilmington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown acceptance facility. Appointments required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "wil-de-2",
      name: "Newark Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "115 E Main St",
      city: "Newark",
      state: "DE",
      zip: "19711",
      phone: "(302) 369-0290",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving University of Delaware area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for University of Delaware students and study abroad applicants. Appointments strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "wil-de-3",
      name: "New Castle Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "112 E 3rd St",
      city: "New Castle",
      state: "DE",
      zip: "19720",
      phone: "(302) 322-1831",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near Delaware Memorial Bridge",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for New Castle residents near the I-295 corridor. Limited hours — book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "wil-de-4",
      name: "Bear Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "315 Middletown Rd",
      city: "Bear",
      state: "DE",
      zip: "19701",
      phone: "(302) 836-0162",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving Bear and Glasgow areas",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for south New Castle County residents. Call to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "wil-de-5",
      name: "Hockessin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 Old Lancaster Pike",
      city: "Hockessin",
      state: "DE",
      zip: "19707",
      phone: "(302) 239-2155",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "Serving Hockessin and Brandywine area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Brandywine Valley and Hockessin residents. Limited passport hours — appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "wil-de-6",
      name: "New Castle County Clerk of Peace",
      type: "County Clerk Passport Acceptance",
      address: "800 N French St",
      city: "Wilmington",
      state: "DE",
      zip: "19801",
      phone: "(302) 395-7799",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "County government center, downtown Wilmington",
      website: "https://www.nccde.org/",
      websiteLabel: "New Castle County",
      notes: "Full-service county facility with extended hours. Appointment recommended. Photo ID and original documents required.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Philadelphia (25–35 min)", fpc: "No — ship from your Wilmington home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo shops in Wilmington sometimes produce generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background — no exceptions.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or incorrect form versions are the #1 reason for rejection at Delaware acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Many Wilmington applicants show up with incorrect checks and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without confirmed flight itinerary, the Philadelphia Passport Agency will not process emergency appointments for Wilmington travelers.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent for Minors",
      description: "For child passports, both parents must appear or provide notarized consent. This is the most common issue for Delaware families.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Wilmington corporate and leisure travelers often assume 2–3 weeks is enough. Routine processing takes 6–13 weeks. Start expedited service at least 4 weeks before departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Wilmington or New Castle County." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Wilmington address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Wilmington, DE?", a: "Fast Passport Center offers emergency passport processing in Wilmington in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. This is the fastest reliable option for Wilmington and New Castle County travelers with urgent international flights out of Philadelphia." },
    { q: "Can I get a same-day passport in Wilmington?", a: "Same-day passports are only available at the Philadelphia Regional Passport Agency (25–35 minutes from Wilmington) for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Wilmington residents." },
    { q: "What is the fastest way to renew my passport in Wilmington?", a: "Fast Passport Center offers the fastest passport renewal in Wilmington with no drive to Philadelphia required. Complete your application online, ship your documents with our prepaid overnight label, and our registered couriers handle the rest. Most Wilmington travelers receive their renewed passport in 2–9 business days." },
    { q: "Do I need to go to Philadelphia for an emergency passport near Wilmington?", a: "Not with Fast Passport Center. While the Philadelphia Passport Agency is only 25–35 minutes from Wilmington and is the closest emergency facility, our registered courier service handles all travel and submission on your behalf. You never have to leave Wilmington." },
    { q: "Where can I get a passport acceptance stamp near Wilmington?", a: "Popular options include the Wilmington Main Post Office (19801), Newark Post Office (19711), New Castle Post Office (19720), Bear Post Office (19701), and the New Castle County Clerk of Peace (19801). All require appointments — we help you schedule." },
    { q: "How do Delaware residents get expedited passport service without a Delaware passport agency?", a: "Delaware has no regional passport agency. However, Fast Passport Center's registered courier service means you don't need one. We hand-deliver your documents to the Philadelphia or Washington DC facilities, achieving 24–48 hour turnaround without any drive required from your Wilmington home or office." },
    { q: "Can you help with a passport for my University of Delaware student?", a: "Yes — we frequently help UD students and parents with expedited passports for study abroad programs. We serve the Newark area and can expedite first-time passports, emergency renewals, and child passports quickly. Call us as soon as you know the program departure date." },
    { q: "What airports do Wilmington travelers use for international flights?", a: "Most Wilmington-area travelers use Philadelphia International Airport (PHL), approximately 25 minutes away. Baltimore-Washington International (BWI) is another popular option. Wilmington Airport (ILG) has limited domestic service. Always verify your passport is valid for at least 6 months beyond your return date before booking." },
    { q: "Can you rush a lost passport replacement in Wilmington?", a: "Yes. If you lost your passport before an upcoming trip, contact Fast Passport Center immediately. We help Wilmington residents navigate the DS-64 lost passport report, prepare replacement documentation, and expedite the new passport in 24–48 hours through our registered courier service." },
    { q: "How much does expedited passport service cost in Wilmington?", a: "Government fees are fixed nationally: $130 for an adult passport book plus a $60 government expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour, 2–9 business day, or standard service. Contact us for a quote based on your travel timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Wilmington", address: "1011 Brandywine Blvd, Wilmington, DE 19802", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Concord Pike", address: "4209 Concord Pike, Wilmington, DE 19803", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Wilmington", address: "815 N Market St, Wilmington, DE 19801", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Wilmington", address: "1300 N Market St, Wilmington, DE 19801", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Newark", address: "400 Ogletown Rd, Newark, DE 19711", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Hockessin", address: "1000 Old Lancaster Pike, Hockessin, DE 19707", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    { name: "DMV — Wilmington", address: "2230 Hessler Blvd, Wilmington, DE 19808", hours: "Mon–Fri: 8AM–4:30PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Newark", address: "723 W Main St, Newark, DE 19702", hours: "Mon–Fri: 8AM–4:30PM", note: "Convenient for University of Delaware area residents. Appointments recommended.", featured: false },
    { name: "DMV — New Castle", address: "2230 Hessler Blvd, New Castle, DE 19720", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves northern and central New Castle County.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "25–35 miles from Wilmington",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international airport for Wilmington travelers. Major hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "4 miles from downtown Wilmington",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Regional airport with domestic flights. Limited international connections. Closest airport to downtown Wilmington.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Flight Info",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "75 miles from Wilmington",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Alternative for southern Delaware travelers and budget airline options. Accessible via I-95 S.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
  ],
};